import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Page, roleLabel, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { Badge, ClassificationTag, LanguagePill, SentimentBadge } from '../../components/shared/Badges';
import { Card, Delta, Kpi, KpiLedger } from '../../components/shared/Card';
import { Avatar, RouteStrip } from '../../components/shared/People';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { BarChart, DivergingRows, HBars, LineChart, useTableToggle } from '../../components/shared/charts';
import {
  PRESENCE_CLASS,
  analyticsApi,
  callDate,
  clockTime,
  hourLabel,
  hourRange,
  isWeekday,
  routeSteps,
  sentimentName,
  severityKey,
  shortDate,
  useAnalytics,
  weekdayDate,
} from '../../services/analyticsApi';
import { presenceChip } from './agentDetailModel';
import { callsLink } from '../calls/callFilters';

const PRESENCE_TONE = { available: 'good', oncall: 'info', busy: 'warning', away: 'warning', offline: 'neutral' };
const PRESENCE_ICON = { available: 'check', oncall: 'phone', busy: 'clock', away: 'clock', offline: 'power' };

function Profile({ data, first }) {
  const a = data.agent;
  const chip = presenceChip(data);
  const status = chip.status;
  const facts = [
    ['Queues', data.queueNames.length ? data.queueNames : ['None']],
    [data.nodeNames.length > 1 ? 'Divisions' : 'Division', data.nodeNames.length ? [data.nodeNames.join(', ')] : ['—']],
    ['Vertolytics role', [data.hasVertolyticsSignIn ? roleLabel([data.role]) : 'Agent (no sign-in)']],
  ];
  return (
    <section className="card">
      <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
        <div className="row" style={{ gap: 14 }}>
          <span style={{ position: 'relative' }}>
            <Avatar name={a.agentName} photoId={a.agentId} large />
            <span className={`presence p-${PRESENCE_CLASS[status] || 'offline'}`} style={{ position: 'absolute', right: 0, bottom: 2, width: 14, height: 14 }} />
          </span>
          <div>
            <div className="section-title">{a.agentName}</div>
            <div className="small sub">{a.agentEmail}</div>
            <div className="row" style={{ marginTop: 6 }}>
              <Badge tone={PRESENCE_TONE[status]} icon={PRESENCE_ICON[status]}>
                {chip.text}
              </Badge>
              {a.languages.map((l) => (
                <LanguagePill key={l} code={l} />
              ))}
            </div>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          {facts.map(([label, lines]) => (
            <div key={label}>
              <div className="caption">{label}</div>
              <div className="small strong" style={{ marginTop: 2 }}>
                {lines.map((line, i) => (
                  <React.Fragment key={line}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Kpis({ data, first }) {
  const a = data.agent;
  const team = data.team;
  const vsTeam = data.teamHandledAverage > 0 ? ((a.answeredCalls - data.teamHandledAverage) / data.teamHandledAverage) * 100 : 0;
  const coverage = a.coverageLanguageCalls;
  return (
    <KpiLedger>
      <Kpi
        label="Calls handled"
        value={fmt.int(a.answeredCalls)}
        meta={
          <>
            <Delta direction={vsTeam >= 0 ? 'up-good' : 'down-bad'}>{`${Math.abs(vsTeam).toFixed(0)}%`}</Delta> {vsTeam >= 0 ? 'above' : 'below'} team average
          </>
        }
      />
      <Kpi label="Answer rate" value={`${a.answerRate.toFixed(1)}%`} meta={`of calls offered to ${first}`} />
      <Kpi label="Average handle time" value={fmt.mmss(a.averageHandleSeconds)} meta={`team ${fmt.mmss(team.averageHandleSeconds)}`} />
      <Kpi label="Transfers" value={fmt.int(a.transferredCalls)} meta={`${a.transferRate.toFixed(1)}% of handled`} />
      <Kpi label="Positive sentiment" value={`${a.positivePercent.toFixed(0)}%`} meta={`team ${team.positivePercent.toFixed(0)}%`} />
      <Kpi label="Spanish calls" value={fmt.int(coverage)} meta={`${a.answeredCalls ? ((coverage / a.answeredCalls) * 100).toFixed(0) : 0}% of ${first}'s calls`} />
    </KpiLedger>
  );
}

function DailyCard({ data, first }) {
  const table = useTableToggle();
  const days = data.daily.filter((d) => isWeekday(d.date) || d.handled > 0);
  return (
    <Card className="c-8" title="Calls handled per day" description={`${first} compared with the ${data.agent.primaryQueueName || 'team'} average`} actions={table.button}>
      <LineChart
        labels={days.map((d) => shortDate(d.date))}
        xName="Day"
        height={230}
        series={[
          { name: data.agent.agentName, values: days.map((d) => d.handled), slot: 1 },
          { name: 'Queue average', values: days.map((d) => Number(d.queueAverage)), color: 'var(--series-other)', dashed: true },
        ]}
        vFmt={(v) => `${fmt.int(v)} calls`}
        showTable={table.on}
      />
    </Card>
  );
}

function HourCard({ data }) {
  const table = useTableToggle();
  const values = data.byHour.map((h) => Number(h.calls));
  const hours = hourRange(values, 8, 17);
  return (
    <Card className="c-5" title="Calls by hour" description="Average weekday" actions={table.button}>
      <BarChart labels={hours.map(hourLabel)} xName="Hour" height={200} vFmt={(v) => Number(v).toFixed(1)} series={[{ name: 'Calls', values: hours.map((h) => values[h] || 0), slot: 1 }]} showTable={table.on} />
    </Card>
  );
}

function RecentCalls({ data }) {
  const navigate = useNavigate();
  const terms = useTerms();
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Caller</th>
            <th>{terms.community}</th>
            <th>Route</th>
            <th>Classifications</th>
            <th className="n">Talk</th>
            <th>Sentiment</th>
            <th>Language</th>
          </tr>
        </thead>
        <tbody>
          {data.recent.map((c) => (
            <tr key={c.callRecordId} className="clickable" tabIndex={0} onClick={() => navigate(`/calls/${c.callRecordId}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/calls/${c.callRecordId}`)}>
              <td className="nowrap">
                <div className="cell-title">{clockTime(c.startTime)}</div>
                <div className="cell-sub">{callDate(c.startTime)}</div>
              </td>
              <td>
                <div className="cell-title">{c.callerName || c.callerNumber}</div>
                {c.callerName && <div className="cell-sub num">{c.callerNumber}</div>}
              </td>
              <td>
                <div>{c.communityName || `Unknown ${terms.communityLower}`}</div>
                <div className="cell-sub">{c.nodeName}</div>
              </td>
              <td>{c.route.length ? <RouteStrip steps={routeSteps(c.route)} /> : <span className="caption">—</span>}</td>
              <td>
                <div className="row-wrap" style={{ gap: 4 }}>
                  {c.classifications.length ? (
                    <>
                      {c.classifications.slice(0, 2).map((x) => (
                        <ClassificationTag key={x.categoryName} category={x.categoryName} severity={severityKey(x.severity)} />
                      ))}
                      {c.classifications.length > 2 && <span className="tag-more">+{c.classifications.length - 2}</span>}
                    </>
                  ) : (
                    <span className="caption">{c.analysisStatus === 'Pending' ? 'Processing' : c.analysisStatus === 'NotApplicable' ? 'No recording' : '—'}</span>
                  )}
                </div>
              </td>
              <td className="n">{c.talkSeconds ? fmt.mmss(c.talkSeconds) : '—'}</td>
              <td>
                <SentimentBadge sentiment={sentimentName(c.sentiment)} />
              </td>
              <td>{c.language ? <LanguagePill code={c.language} /> : <span className="caption">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AgentDetailPage() {
  const { id } = useParams();
  const h = useHierarchyFilter();
  const { data, error, retry } = useAnalytics(() => analyticsApi.agent(id, h.params), JSON.stringify([id, h.params]));
  const name = data?.agent.agentName || 'Agent';
  const first = name.split(' ')[0];

  let body;
  if (error && error.response?.status === 404)
    body = <EmptyState icon="headset" title="Agent not found" body="This agent isn't in your organization's Teams queues, or you don't have access to their calls." />;
  else if (error) body = <ErrorState title="Agent couldn't load" body="Vertolytics couldn't reach the analytics service." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={420} />;
  else if (!data.agent.totalCalls)
    body = (
      <div className="stack-lg">
        <Profile data={data} first={first} />
        <EmptyState icon="headset" title={`${first} hasn't handled calls in this period`} body={`${first} is a member of ${data.queueNames.join(' and ') || 'no queues'}. Try a wider date range.`} />
      </div>
    );
  else
    body = (
      <div className="stack-lg">
        <Profile data={data} first={first} />
        <Kpis data={data} first={first} />
        <div className="grid">
          <DailyCard data={data} first={first} />
          <Card className="c-4" title="What callers needed" description={`Classifications on ${first}'s analyzed calls`}>
            {data.mix.length ? <HBars labelAbove labelWidth="1.9fr" items={data.mix.map((m) => ({ label: m.categoryName, value: m.calls }))} /> : <div className="caption">No analyzed calls yet.</div>}
          </Card>
          <HourCard data={data} />
          <Card className="c-7" title="Caller sentiment at end of call" description="Share of analyzed calls per day">
            {data.sentiment.length ? (
              <DivergingRows
                keys={[
                  { name: 'Negative', color: 'var(--div-neg-2)' },
                  { name: 'Neutral', color: 'var(--div-mid)' },
                  { name: 'Positive', color: 'var(--div-pos-2)' },
                ]}
                rows={data.sentiment.map((d) => {
                  const total = d.negative + d.neutral + d.positive;
                  return { label: weekdayDate(d.date), values: [d.negative, d.neutral, d.positive], right: `${Math.round((d.positive / total) * 100)}% positive` };
                })}
              />
            ) : (
              <div className="caption">No analyzed calls yet.</div>
            )}
          </Card>
          <Card
            className="c-12"
            flush
            title="Recent calls"
            description="Most recent first"
            actions={
              <Link className="btn btn-ghost btn-sm" to={callsLink({ agentIds: [id], agentName: data?.agent.agentName })}>
                Open in Calls
              </Link>
            }
          >
            <RecentCalls data={data} />
          </Card>
        </div>
      </div>
    );

  return (
    <Page
      title={name}
      documentTitle={name}
      crumbs={[{ label: 'Agents', to: '/agents' }, { label: name }]}
      exportConfig={{ name: `${name} calls`, endpoint: '/export/datasets/Calls', params: { agentIds: id, title: `${name} calls` } }}
      actions={
        <Link className="btn" to={callsLink({ agentIds: [id], agentName: data?.agent.agentName })}>
          <Icon name="phone" />
          View calls
        </Link>
      }
    >
      {body}
    </Page>
  );
}
